import React from 'react';
import { motion } from 'framer-motion';
import { FiGithub, FiExternalLink, FiStar } from 'react-icons/fi';

const projectLogoMap = {
  'Nutri AI': '/images/project/nutri-chat-logo.png',
  'SurgeSense': '/images/project/SurgeSnese.png',
  'Know Your Credit': '/images/project/kyc-logo.jpg',
  'Docsy': '/images/project/favicon.ico',
};

const Projects = ({ projects }) => {
  return (
    <section id="projects" className="section-padding bg-[#F5F2E8] dark:bg-gray-900">
      <div className="container-max">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Featured Projects
          </h2>
          <div className="w-24 h-1 bg-blue-600 mx-auto mb-8"></div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project, index) => {
            const techList = project.tech || project.technologies || [];

            return (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                whileHover={{ y: -3 }}
                className="card group overflow-hidden flex flex-col"
              >
                <div className="space-y-4 flex-1 flex flex-col">
                  {/* Title + Links row */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                      {projectLogoMap[project.title] ? (
                        <img
                          src={`${process.env.PUBLIC_URL}${projectLogoMap[project.title]}`}
                          alt={`${project.title} logo`}
                          className="w-10 h-10 rounded-lg object-contain bg-white dark:bg-gray-700 p-1 flex-shrink-0"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-lg flex-shrink-0 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-200 flex items-center justify-center text-sm font-bold">
                          {project.title.charAt(0)}
                        </div>
                      )}

                      <div className="min-w-0">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                          {project.title}
                        </h3>
                        {project.tagline && (
                          <p className="text-blue-600 dark:text-blue-400 text-sm font-medium mt-1">
                            {project.tagline}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 flex-shrink-0 pt-1">
                      {project.github && (
                        <a
                          href={project.github}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                          title="Source Code"
                        >
                          <FiGithub size={18} />
                        </a>
                      )}
                      {project.demo && (
                        <a
                          href={project.demo}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                          title="Live Demo"
                        >
                          <FiExternalLink size={18} />
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Highlight badge */}
                  {project.highlight && (
                    <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg px-3 py-2">
                      <FiStar className="text-amber-500 mt-0.5 flex-shrink-0" size={14} />
                      <span className="text-xs text-amber-700 dark:text-amber-300 leading-tight">
                        {project.highlight}
                      </span>
                    </div>
                  )}

                  {/* Description */}
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    {project.description}
                  </p>

                  {/* Features */}
                  {project.features && project.features.length > 0 && (
                    <ul className="space-y-1.5 list-disc pl-5 marker:text-gray-500 dark:marker:text-gray-400">
                      {project.features.map((feature, i) => (
                        <li key={i} className="text-sm text-gray-600 dark:text-gray-300">
                          {feature}
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Tech tags */}
                  <div className="flex flex-wrap gap-2">
                    {techList.map((tech) => (
                      <span
                        key={tech}
                        className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs rounded-full"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>

                  {/* Footer: category */}
                  <div className="pt-4 mt-auto">
                    <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                      {project.category}
                    </span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Projects;